import {
  type InjectionValuesRecord,
  type InjectionKeysRecord,
  CreateInjectionKeysRecord,
  injectRequiredWithSelf,
  checkValuesAlreadyProvided, 
  useRunWithContextFn
} from "@mcwd/util-vue";
import { type ILogger } from "../../utils/Logger/ILogger.js";
import type { AnyFormSettingsKey } from "../form-settings/form-experience/form-settings-keys.js";
import { BaseFormLogger, SecondButtonDefaultStyle } from "../base-form-logger.js";
import { provide } from "vue";

type UseFormLoggerInjectionValues = InjectionValuesRecord<{
  logger: ILogger,
  getVerboseLogger: (verboseDebugTag: string) => ILogger
}>;

type UseFormLoggerInjectionKeys = InjectionKeysRecord<UseFormLoggerInjectionValues>;

const UseFormLoggerInjectionKeys = CreateInjectionKeysRecord<UseFormLoggerInjectionKeys>({
  logger: "logger",
  getVerboseLogger: "getVerboseLogger"
});

export function initFormLogger(formSettingsKey: AnyFormSettingsKey) {
  if (checkValuesAlreadyProvided(UseFormLoggerInjectionKeys)) {
    return;
  }
  const logger = BaseFormLogger.getChildLogger({
    tags: { category: "Forms" },
    prefixStrings: [formSettingsKey],
    prefixStyles: [SecondButtonDefaultStyle]
  });

  const getVerboseLogger = (verboseDebugTag: string) => {
    return logger.withTags({ verboseDebug: verboseDebugTag });
  };

  provide(UseFormLoggerInjectionKeys.logger, logger);
  provide(UseFormLoggerInjectionKeys.getVerboseLogger, getVerboseLogger);
}

export function useFormLogger({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  try {
    callerLabel ??= "useFormLogger";
    const runWithContextFn = useRunWithContextFn();
    const logger = runWithContextFn(() => injectRequiredWithSelf(UseFormLoggerInjectionKeys.logger));
    const getVerboseLogger = injectRequiredWithSelf(UseFormLoggerInjectionKeys.getVerboseLogger);
    return { logger, getVerboseLogger };
  }
  catch (err) {
    BaseFormLogger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}